import type { ReactNode } from 'react';
import { PageContainer } from '../PageContainer/PageContainer';
import { SplitLayout, type SplitAsideWidth } from '../layouts/SplitLayout';

export type SearchResultsTemplateProps = {
  breadcrumbs?: ReactNode;
  /** The H1: the live sentence for this search. Omit it only with `header`. */
  title?: ReactNode;
  /** A line under the H1 (the count and basis). Keep it to one line; longer write-ups go in `below`. */
  subtitle?: ReactNode;
  /**
   * How much room the head takes. `compact` (default): a 32px title, the lede as one small line, the
   * figures and the view controls on one row, so the first cards start near the top of the screen.
   * `roomy`: the 52px display title, a place card and a row of StatTiles.
   */
  density?: 'compact' | 'roomy';
  /** `roomy` only. The place card (photo with a small Readout instead of text over the photo), when the search is a place. */
  placeCard?: ReactNode;
  /**
   * A page's own header block (it owns its H1), used instead of the breadcrumbs, title, subtitle and
   * place card when a page's header is richer than the slots (a data-viz hero). The stats row, chips
   * row, alert and results still follow.
   */
  header?: ReactNode;
  /** `roomy` only. Up to four StatTiles, each with its basis. */
  stats?: ReactNode;
  /** `compact` only. A FigureStrip: the page's figures in one line. */
  figures?: ReactNode;
  /**
   * `compact` only. The Gross / Take-home and Cards / Map SegmentedControls. From lg they sit on the
   * figures' row, at the right; below lg they take their own row.
   */
  controls?: ReactNode;
  /**
   * Active FilterPills and quick FilterChips (with `roomy`, also the SegmentedControls). In `compact`
   * the row is left out when it has nothing in it.
   */
  filters?: ReactNode;
  /** The AlertBanner for this search (`compact` on a compact page). */
  alert?: ReactNode;
  /** JobCards. */
  results: ReactNode;
  /**
   * Wrap `results` in the card grid (default). Turn it off when the caller lays out its own list of
   * cards (a grid that adapts to its own width, a table, an empty state).
   */
  resultsGrid?: boolean;
  pagination?: ReactNode;
  /** The right rail: the mini map and the employers hiring most. */
  rail?: ReactNode;
  /** Width of the rail from lg: 320px (`side`, default) or 384px (`wide`). */
  railWidth?: Extract<SplitAsideWidth, 'side' | 'wide'>;
  /** Keep the rail in view while the results scroll (default). Turn it off for a rail taller than the screen. */
  stickyRail?: boolean;
  /** Full-width sections after the results (TemplateSections: what this search is, nearby places, questions). */
  below?: ReactNode;
  className?: string;
};

/**
 * The search results page. Count the yellow: the search button and the one "new this week" figure,
 * nothing else. Selection is ink; employer colours lead.
 */
export function SearchResultsTemplate({
  breadcrumbs,
  title,
  subtitle,
  density = 'compact',
  placeCard,
  header,
  stats,
  figures,
  controls,
  filters,
  alert,
  results,
  resultsGrid = true,
  pagination,
  rail,
  railWidth = 'side',
  stickyRail = true,
  below,
  className,
}: SearchResultsTemplateProps) {
  const compact = density === 'compact';
  return (
    <PageContainer width="wide" className={className}>
      <div className={compact ? 'flex flex-col gap-4 py-4 lg:py-jc-block' : 'flex flex-col gap-jc-block py-jc-block'}>
        {header ?? (
          <>
            {compact ? (
              <header className="flex flex-col gap-1">
                {breadcrumbs}
                <h1 className="type-title-lg text-jc-ink">{title}</h1>
                {subtitle ? <p className="m-0 text-sm text-jc-ink-secondary">{subtitle}</p> : null}
              </header>
            ) : (
              <>
                {breadcrumbs}
                <header className="grid gap-5 lg:grid-cols-3">
                  <div
                    className={placeCard ? 'flex flex-col gap-2 lg:col-span-2' : 'flex flex-col gap-2 lg:col-span-3'}
                  >
                    <h1 className="type-display text-jc-ink">{title}</h1>
                    {subtitle ? <p className="text-base text-jc-ink-secondary">{subtitle}</p> : null}
                  </div>
                  {placeCard}
                </header>
              </>
            )}
          </>
        )}
        {compact ? (
          figures || controls ? (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
              {figures ? <div className="min-w-0">{figures}</div> : <span />}
              {/* Right-aligned at every width: beside the figures from lg, on their own row below. They
                  wrap rather than overflow: right-aligned content that overflows is lost off the
                  left edge on the narrowest phones. */}
              {controls ? <div className="flex flex-wrap items-center justify-end gap-2">{controls}</div> : null}
            </div>
          ) : null
        ) : stats ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{stats}</div>
        ) : null}
        {filters ? <div className="flex flex-wrap items-center gap-2 empty:hidden">{filters}</div> : null}
        {alert}
        <SplitLayout aside={rail} asideWidth={railWidth} stickyAside={stickyRail}>
          <div className="flex flex-col gap-jc-block">
            {/* Phones: the result cards take an 8px gutter instead of the page's 16px, so the text in
                them gets the width (the head, filters and pagination keep the page gutter). */}
            {resultsGrid ? (
              <div className="grid gap-5 max-sm:-mx-2 max-sm:gap-3 md:grid-cols-2 2xl:grid-cols-3">{results}</div>
            ) : (
              <div className="max-sm:-mx-2">{results}</div>
            )}
            {pagination}
          </div>
        </SplitLayout>
        {below ? (
          <div className={compact ? 'mt-jc-section flex flex-col gap-jc-block empty:hidden' : 'contents'}>{below}</div>
        ) : null}
      </div>
    </PageContainer>
  );
}
